import { ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import FullModalAccordion from '@components/FullModalAccordion';
import PlanCard from '@components/PlanCard';
import SkeletonCard from '@components/PlanCard/SkeletonCard';
import QuoteCard from '@components/QuoteCard';
import ShouldKnowSection from '@components/ShouldKnowSection';
import useQuoter from '@hooks/useQuoter';
import { InsuranceT } from '@segurodeceso/core/domain/lead';

export type LeadPlanT = Pick<
  InsuranceT,
  'productCode' | 'planId' | 'planCode' | 'planName' | 'coverages' | 'prices' | 'sponsor'
>;

interface QuoterPropsI {
  preLoading: boolean;
  selectedPlan: number;
  setSelectedPlan: (plan: LeadPlanT) => void;
  noInsuredMembers: number;
  setNoInsuredMembers: (noInsuredMembers: number) => void;
}

type DataFullModalT = {
  planCode: string;
};

const Quoter = ({
  preLoading,
  selectedPlan,
  setSelectedPlan,
  noInsuredMembers,
  setNoInsuredMembers,
}: QuoterPropsI): ReactElement => {
  const { t } = useTranslation();

  const {
    plans,
    loading,
    modalOpen,
    setModalOpen,
    getClickedPlan,
    getLowestPricePlan,
    onMoreDetails,
    onCardClick,
  } = useQuoter(noInsuredMembers, selectedPlan, setSelectedPlan);

  const dataFullModal: DataFullModalT[] = t('planFullModal', {
    returnObjects: true,
  }) as DataFullModalT[];

  const findPlanFullModalKey = () => {
    const planCode = getClickedPlan()?.planCode;
    const index = dataFullModal.findIndex((plan) => plan.planCode === planCode);
    const key = `planFullModal.${index.toString()}`;
    return key;
  };

  return (
    <>
      <div className="container mx-auto max-w-[902px] px-0">
        <div className="text-center flex flex-col gap-4">
          <h1 className="font-extrabold leading-8 text-[24px] md:leading-heading/xl md:text-heading/xl">
            <Trans t={t} i18nKey="quoter.title" />
          </h1>
          <p className="mt-4m ax-w-[902px] text-color/neutral/80 text-paragraph/s leading-paragraph/s font-normal md:text-heading/s md:leading-heading/s">
            <Trans
              t={t}
              i18nKey="quoter.description"
              components={{ span: <span className="font-semibold md:font-bold" /> }}
            />
          </p>
        </div>
        <div className="flex justify-between mt-4 md:mt-14 gap-4 flex-col lg:flex-row">
          <div className="flex flex-col w-full gap-10">
            <QuoteCard count={noInsuredMembers} setCount={setNoInsuredMembers} />
            <ShouldKnowSection />
          </div>
          <div className="flex flex-1 items-center flex-col mt-4 lg:mt-0 lg:flex-row justify-between gap-6 md:gap-4">
            {!loading && !preLoading && plans?.length > 0 ? (
              plans.map((plan, index) => {
                return (
                  <PlanCard
                    key={index}
                    plan={plan}
                    isSelected={selectedPlan === plan.planId}
                    onCardClick={() => onCardClick(plan)}
                    onMoreDetails={() => onMoreDetails(plan)}
                  />
                );
              })
            ) : (
              <SkeletonCard numberOfCards={2} />
            )}
          </div>
        </div>
      </div>
      <FullModalAccordion
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        i18key={findPlanFullModalKey()}
        lowestPrice={getLowestPricePlan(getClickedPlan()?.planCode)}
      />
    </>
  );
};

export default Quoter;
